import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";

interface Props {
  workspaceId: string;
}

interface StudentStat {
  userId: string;
  email: string;
  displayName: string;
  capital: number;
  reputation: number;
  taskCount: number;
  hubCount: number;
}

interface HubStat {
  hub: string;
  count: number;
}

interface EventStat {
  date: string;
  count: number;
}

function getRankName(rep: number) {
  if (rep >= 81) return "부장";
  if (rep >= 61) return "과장";
  if (rep >= 41) return "대리";
  if (rep >= 21) return "사원";
  return "인턴";
}

function getRankColor(rep: number) {
  if (rep >= 81) return "#EAB308";
  if (rep >= 61) return "#E8375F";
  if (rep >= 41) return "#FF9F43";
  if (rep >= 21) return "#7A9BFF";
  return "#8B91A8";
}

export default function WorkspaceStatsDashboard({ workspaceId }: Props) {
  const [students, setStudents] = useState<StudentStat[]>([]);
  const [hubStats, setHubStats] = useState<HubStat[]>([]);
  const [eventStats, setEventStats] = useState<EventStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchStats = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);

    // 1. 학생 목록 + company_states
    const { data: wm } = await supabase
      .from("workspace_members")
      .select("user_id")
      .eq("workspace_id", workspaceId);

    if (!wm || wm.length === 0) { setLoading(false); return; }

    const userIds = wm.map(m => m.user_id);

    const [{ data: states }, { data: profiles }, { data: completions }, { data: events }] = await Promise.all([
      supabase.from("company_states").select("user_id, capital, reputation").eq("workspace_id", workspaceId),
      supabase.from("profiles").select("id, email, display_name").in("id", userIds),
      supabase.from("task_completions").select("user_id, hub").eq("workspace_id", workspaceId),
      supabase.from("event_logs").select("user_id, created_at").eq("workspace_id", workspaceId).order("created_at", { ascending: false }).limit(100),
    ]);

    const stateMap: Record<string, { capital: number; reputation: number }> = {};
    (states ?? []).forEach(s => { stateMap[s.user_id] = s; });

    const profMap: Record<string, { email: string; display_name: string }> = {};
    (profiles ?? []).forEach(p => { profMap[p.id] = p; });

    const taskMap: Record<string, Set<string>> = {};
    const hubMap: Record<string, number> = {};
    (completions ?? []).forEach(c => {
      if (!taskMap[c.user_id]) taskMap[c.user_id] = new Set();
      taskMap[c.user_id].add(c.user_id + "_" + c.hub);
      const hub = c.hub?.toLowerCase().replace(/-hub$/, "").replace(/hub$/, "").trim() ?? "기타";
      hubMap[hub] = (hubMap[hub] ?? 0) + 1;
    });

    const taskCountMap: Record<string, number> = {};
    const hubCountMap: Record<string, number> = {};
    (completions ?? []).forEach(c => {
      taskCountMap[c.user_id] = (taskCountMap[c.user_id] ?? 0) + 1;
    });

    // hub count per user
    const userHubs: Record<string, Set<string>> = {};
    (completions ?? []).forEach(c => {
      if (!userHubs[c.user_id]) userHubs[c.user_id] = new Set();
      userHubs[c.user_id].add(c.hub ?? "기타");
    });

    const studentStats: StudentStat[] = userIds.map(uid => ({
      userId: uid,
      email: profMap[uid]?.email ?? "-",
      displayName: profMap[uid]?.display_name ?? "익명",
      capital: stateMap[uid]?.capital ?? 0,
      reputation: stateMap[uid]?.reputation ?? 0,
      taskCount: taskCountMap[uid] ?? 0,
      hubCount: userHubs[uid]?.size ?? 0,
    }));

    // Hub stats
    const hubLabels: Record<string, string> = {
      ar: "A&R", concert: "공연기획", foundation: "재단",
      ip: "IP 스튜디오", campaign: "캠페인",
    };
    const hs = Object.entries(hubMap)
      .map(([h, c]) => ({ hub: hubLabels[h] ?? h, count: c }))
      .sort((a, b) => b.count - a.count);

    // Event stats (last 7 days)
    const evMap: Record<string, number> = {};
    (events ?? []).forEach(e => {
      const d = new Date(e.created_at).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" });
      evMap[d] = (evMap[d] ?? 0) + 1;
    });
    const es = Object.entries(evMap)
      .map(([date, count]) => ({ date, count }))
      .slice(0, 7)
      .reverse();

    setStudents(studentStats);
    setHubStats(hs);
    setEventStats(es);
    setLastRefresh(new Date());
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="space-y-5">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-36 bg-white/[0.02] rounded-xl animate-pulse border border-white/[0.04]" />
        ))}
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <i className="ri-group-line text-3xl text-[#222222] block mb-3"></i>
        <p className="text-[#5A5E72] text-sm">이 워크스페이스에 입장한 학생이 없어요</p>
      </div>
    );
  }

  const avgCapital = Math.round(students.reduce((a, s) => a + s.capital, 0) / students.length);
  const avgRep = Math.round(students.reduce((a, s) => a + s.reputation, 0) / students.length);
  const totalTasks = students.reduce((a, s) => a + s.taskCount, 0);
  const topStudent = [...students].sort((a, b) => b.reputation - a.reputation)[0];

  // 자본금 분포 (빈 구간)
  const capitalBins = [
    { label: "0~1억", min: 0, max: 10000 },
    { label: "1억~3억", min: 10000, max: 30000 },
    { label: "3억~5억", min: 30000, max: 50000 },
    { label: "5억~10억", min: 50000, max: 100000 },
    { label: "10억+", min: 100000, max: Infinity },
  ];
  const capitalDist = capitalBins.map(bin => ({
    label: bin.label,
    count: students.filter(s => s.capital >= bin.min && s.capital < bin.max).length,
  }));

  // 직급 분포
  const rankDist = [
    { name: "부장", color: "#EAB308", count: students.filter(s => s.reputation >= 81).length },
    { name: "과장", color: "#E8375F", count: students.filter(s => s.reputation >= 61 && s.reputation < 81).length },
    { name: "대리", color: "#FF9F43", count: students.filter(s => s.reputation >= 41 && s.reputation < 61).length },
    { name: "사원", color: "#7A9BFF", count: students.filter(s => s.reputation >= 21 && s.reputation < 41).length },
    { name: "인턴", color: "#8B91A8", count: students.filter(s => s.reputation < 21).length },
  ];

  const maxCapDist = Math.max(...capitalDist.map(d => d.count), 1);
  const maxHubCount = Math.max(...hubStats.map(h => h.count), 1);
  const maxEvCount = Math.max(...eventStats.map(e => e.count), 1);

  return (
    <div className="space-y-5">
      {/* 새로고침 */}
      <div className="flex items-center justify-between">
        <div className="text-[#3D3D3D] text-[10px]">
          마지막 업데이트: {lastRefresh.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
        </div>
        <button type="button" onClick={fetchStats}
          className="flex items-center gap-1.5 text-xs text-[#555555] hover:text-[#888888] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line text-sm"></i>새로고침
        </button>
      </div>

      {/* 핵심 지표 요약 */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: "총 학생 수", value: students.length, unit: "명", icon: "ri-group-line", color: "#7A9BFF" },
          { label: "평균 자본금", value: avgCapital.toLocaleString(), unit: "만원", icon: "ri-money-dollar-circle-line", color: "#00C896" },
          { label: "평균 직급점수", value: avgRep, unit: "pt", icon: "ri-award-line", color: "#FF9F43" },
          { label: "총 완료 태스크", value: totalTasks, unit: "건", icon: "ri-checkbox-circle-line", color: "#E8375F" },
        ].map(s => (
          <div key={s.label} className="bg-[#111112] rounded-xl border border-white/[0.05] px-4 py-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
              </div>
              <span className="text-[#555555] text-[10px] uppercase tracking-widest">{s.label}</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</span>
              <span className="text-[#444444] text-xs">{s.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-5">
        {/* 직급 분포 */}
        <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-5">
          <div className="text-[#888888] text-xs font-semibold mb-4 flex items-center gap-2">
            <i className="ri-bar-chart-grouped-line text-sm text-[#555555]"></i>직급 분포
          </div>
          <div className="space-y-2.5">
            {rankDist.map(r => (
              <div key={r.name} className="flex items-center gap-3">
                <span className="text-[10px] font-semibold w-8 shrink-0" style={{ color: r.color }}>{r.name}</span>
                <div className="flex-1 h-5 bg-white/[0.03] rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md flex items-center pl-2 transition-all duration-700"
                    style={{
                      width: `${students.length > 0 ? (r.count / students.length) * 100 : 0}%`,
                      backgroundColor: `${r.color}25`,
                      borderRight: r.count > 0 ? `2px solid ${r.color}60` : "none",
                      minWidth: r.count > 0 ? 24 : 0,
                    }}
                  >
                    {r.count > 0 && (
                      <span className="text-[9px] font-bold" style={{ color: r.color }}>{r.count}</span>
                    )}
                  </div>
                </div>
                <span className="text-[#3D3D3D] text-[10px] w-8 text-right shrink-0">{r.count}명</span>
              </div>
            ))}
          </div>

          {/* 최고 성과 학생 */}
          {topStudent && (
            <div className="mt-4 pt-3 border-t border-white/[0.04]">
              <div className="text-[#3D3D3D] text-[10px] uppercase tracking-widest mb-2">최고 직급점수</div>
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: `${getRankColor(topStudent.reputation)}20`, color: getRankColor(topStudent.reputation) }}
                >
                  {topStudent.displayName[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[#CCCCCC] text-xs font-medium truncate">{topStudent.displayName}</div>
                  <div className="text-[#555555] text-[10px]">{getRankName(topStudent.reputation)} · {topStudent.reputation}pt</div>
                </div>
                <i className="ri-medal-line text-yellow-500 text-sm shrink-0"></i>
              </div>
            </div>
          )}
        </div>

        {/* 자본금 분포 */}
        <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-5">
          <div className="text-[#888888] text-xs font-semibold mb-4 flex items-center gap-2">
            <i className="ri-money-dollar-circle-line text-sm text-[#555555]"></i>자본금 분포
          </div>
          <div className="space-y-2.5">
            {capitalDist.map(bin => (
              <div key={bin.label} className="flex items-center gap-3">
                <span className="text-[10px] text-[#555555] w-16 shrink-0">{bin.label}</span>
                <div className="flex-1 h-5 bg-white/[0.03] rounded-md overflow-hidden">
                  <div
                    className="h-full rounded-md flex items-center pl-2 transition-all duration-700 bg-[#00C896]/20"
                    style={{
                      width: `${(bin.count / maxCapDist) * 100}%`,
                      borderRight: bin.count > 0 ? "2px solid rgba(0,200,150,0.4)" : "none",
                      minWidth: bin.count > 0 ? 24 : 0,
                    }}
                  >
                    {bin.count > 0 && (
                      <span className="text-[9px] font-bold text-[#00C896]">{bin.count}</span>
                    )}
                  </div>
                </div>
                <span className="text-[#3D3D3D] text-[10px] w-8 text-right shrink-0">{bin.count}명</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        {/* 허브별 태스크 완료 */}
        <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-5">
          <div className="text-[#888888] text-xs font-semibold mb-4 flex items-center gap-2">
            <i className="ri-task-line text-sm text-[#555555]"></i>허브별 태스크 완료
          </div>
          {hubStats.length === 0 ? (
            <div className="py-8 text-center text-[#333333] text-xs">완료된 태스크가 없어요</div>
          ) : (
            <div className="space-y-2.5">
              {hubStats.map((h, i) => {
                const colors = ["#7A9BFF", "#FF9F43", "#E8375F", "#00C896", "#A78BFA"];
                const c = colors[i % colors.length];
                return (
                  <div key={h.hub} className="flex items-center gap-3">
                    <span className="text-[10px] text-[#555555] w-20 shrink-0 truncate">{h.hub}</span>
                    <div className="flex-1 h-5 bg-white/[0.03] rounded-md overflow-hidden">
                      <div
                        className="h-full rounded-md flex items-center pl-2 transition-all duration-700"
                        style={{
                          width: `${(h.count / maxHubCount) * 100}%`,
                          backgroundColor: `${c}20`,
                          borderRight: `2px solid ${c}50`,
                          minWidth: 24,
                        }}
                      >
                        <span className="text-[9px] font-bold" style={{ color: c }}>{h.count}</span>
                      </div>
                    </div>
                    <span className="text-[#3D3D3D] text-[10px] w-8 text-right shrink-0">{h.count}건</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 이벤트 발생 추이 */}
        <div className="bg-[#111112] rounded-xl border border-white/[0.05] p-5">
          <div className="text-[#888888] text-xs font-semibold mb-4 flex items-center gap-2">
            <i className="ri-pulse-line text-sm text-[#555555]"></i>이벤트 대응 추이
          </div>
          {eventStats.length === 0 ? (
            <div className="py-8 text-center text-[#333333] text-xs">이벤트 기록이 없어요</div>
          ) : (
            <div className="flex items-end gap-2 h-28">
              {eventStats.map(e => (
                <div key={e.date} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[9px] text-[#4B6CF7] font-bold">{e.count}</div>
                  <div
                    className="w-full rounded-t-sm bg-[#4B6CF7]/30 border-t border-[#4B6CF7]/50 transition-all duration-500"
                    style={{ height: `${Math.max(8, (e.count / maxEvCount) * 72)}px` }}
                  />
                  <div className="text-[8px] text-[#3D3D3D] text-center leading-tight">{e.date}</div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[#3D3D3D] text-[10px] mt-3">최근 이벤트 대응 기록 기준</div>
        </div>
      </div>

      {/* 학생별 상세 순위표 */}
      <div className="bg-[#111112] rounded-xl border border-white/[0.05] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.04]">
          <span className="text-[#888888] text-xs font-semibold">학생별 성과 순위</span>
          <span className="text-[#3D3D3D] text-[10px] ml-2">직급점수 기준 내림차순</span>
        </div>
        <div className="divide-y divide-white/[0.03]">
          {[...students].sort((a, b) => b.reputation - a.reputation).map((s, i) => (
            <div key={s.userId} className="px-5 py-2.5 flex items-center gap-3">
              <span className={`text-[10px] font-bold w-5 shrink-0 tabular-nums ${i < 3 ? "text-yellow-500" : "text-[#3D3D3D]"}`}>
                {i + 1}
              </span>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                style={{ backgroundColor: `${getRankColor(s.reputation)}15`, color: getRankColor(s.reputation) }}
              >
                {s.displayName[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[#CCCCCC] text-xs font-medium truncate">{s.displayName}</div>
                <div className="text-[#555555] text-[10px] truncate">{s.email}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-bold tabular-nums" style={{ color: getRankColor(s.reputation) }}>
                  {getRankName(s.reputation)}
                </div>
                <div className="text-[#3D3D3D] text-[9px]">{s.reputation}pt</div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <div className="text-[#00C896] text-xs font-bold tabular-nums">{s.capital.toLocaleString()}</div>
                <div className="text-[#3D3D3D] text-[9px]">만원</div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <div className="text-[#7A9BFF] text-xs font-bold tabular-nums">{s.taskCount}</div>
                <div className="text-[#3D3D3D] text-[9px]">건</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
